import { SocialNetworksId } from "@/interfaces";
import { Pagination } from "./Page";

const ENGAGEMENT_METRICS = [
  "like_count",
  "comment_count",
  "view_count",
  "share_count",
  "love_count",
  "wow_count",
  "haha_count",
  "sorry_count",
  "anger_count",
  "celebrate_count",
  "insightful_count",
  "support_count",
  "total_reactions",
  "impression_count",
  "reach_count",
  "click_count",
];

const FOLLOWER_METRICS = ["follower_count", "follower_change", "follower_change_percent"] as const;

export type EngagementMetric = (typeof ENGAGEMENT_METRICS)[number];
export type FollowerMetric = (typeof FOLLOWER_METRICS)[number];

export function isFollowerMetric(metric: string): metric is FollowerMetric {
  return (FOLLOWER_METRICS as readonly string[]).includes(metric);
}

export function isEngagementMetric(metric: string): metric is EngagementMetric {
  return (ENGAGEMENT_METRICS as readonly string[]).includes(metric);
}

export function isVideoPageMetric(metric: string): metric is LeaderboardMetric {
  return ["view_count", "impression_count", "reach_count", "click_count"].includes(metric);
}

export interface Leaderboard {
  title: string;
  entityType: EntityType;
  metric?: LeaderboardMetric;
  startTimeKey?: string;
}

export type LeaderboardMetric = EngagementMetric | FollowerMetric | "post_count";

export type EntityType =
  | "player"
  | "post"
  | "prompt"
  | "campaign"
  | "region"
  | "branch"
  | "social_identity"
  | "video_script"
  | "content_page"
  | "coach";

interface LeaderboardResultBase {
  id: number;
  score: number;
  type: EntityType;

  angerCount?: number | null;
  celebrateCount?: number | null;
  hahaCount?: number | null;
  insightfulCount?: number | null;
  likeCount?: number | null;
  loveCount?: number | null;
  sorryCount?: number | null;
  supportCount?: number | null;
  wowCount?: number | null;
  totalReactions?: number | null;

  impressionCount?: number | null;
  reachCount?: number | null;
  shareCount?: number | null;
  viewCount?: number | null;
  clickCount?: number | null;
  commentCount?: number | null;
}

export interface PlayerResult extends LeaderboardResultBase {
  email: string;
  firstName: string | null;
  lastName: string | null;
  internalIdentifier: string | null;
  photoUrl: string | null;
}

export interface PostResult extends LeaderboardResultBase {
  // Player info
  email: string;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  internalIdentifier: string | null;
  playerId: number;

  // Post info
  postedCaption: string | null;
  postedTimestamp: string;
  externalPostUrl: string | null;
  socialNetworkId: SocialNetworksId;
}

export interface PromptResult extends LeaderboardResultBase {
  title: string;
}

export interface CampaignResult extends LeaderboardResultBase {
  name: string;
}

export interface CoachResult extends LeaderboardResultBase {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface RegionResult extends LeaderboardResultBase {
  name: string;
}

export interface BranchResult extends LeaderboardResultBase {
  name: string;
}

export interface SocialIdentityResult extends LeaderboardResultBase {
  email: string;
  firstName: string | null;
  lastName: string | null;
  username?: string;
  displayName?: string;
  publicUrl?: string;
  socialNetworkId: SocialNetworksId;
}

export interface ContentPageResult extends LeaderboardResultBase {
  title: string;
  createdDate: string;
  url: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export interface VideoScriptResult extends LeaderboardResultBase {
  title: string;
}

interface LeaderboardResponseBase {
  metric: LeaderboardMetric;
  startTime: string | null;
  endTime: string | null;
  pagination: Pagination;
}

export type LeaderboardResult =
  | PlayerResult
  | PromptResult
  | CampaignResult
  | PostResult
  | RegionResult
  | BranchResult
  | SocialIdentityResult
  | ContentPageResult
  | VideoScriptResult
  | CoachResult;

interface PlayerLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "player";
  results: PlayerResult[];
}

interface SocialIdentityLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "social_identity";
  results: SocialIdentityResult[];
}

interface PostLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "post";
  results: PostResult[];
}

interface PromptLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "prompt";
  results: PromptResult[];
}

interface CampaignLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "campaign";
  results: CampaignResult[];
}

interface RegionLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "region";
  results: RegionResult[];
}

interface BranchLeaderboardResponse extends LeaderboardResponseBase {
  entityType: "branch";
  results: BranchResult[];
}

export type LeaderboardResponse =
  | PlayerLeaderboardResponse
  | PostLeaderboardResponse
  | PromptLeaderboardResponse
  | CampaignLeaderboardResponse
  | RegionLeaderboardResponse
  | BranchLeaderboardResponse
  | SocialIdentityLeaderboardResponse;

export interface LeaderboardQuery {
  coachId: string | number | undefined;
  playerIds?: (string | number)[] | undefined;
  entityType: EntityType;
  metric: LeaderboardMetric;
  startTime: Date | undefined;
  page: number | undefined;
  perPage: number | null | undefined;
}
